import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Adds a whole outfit to the cart in one request.
 *
 * Size matching: the block renders a map of this product's variant ids to
 * their size option value, and for each outfit product a map of size value to
 * an available variant id. At click time the currently selected variant on the
 * page resolves to a size, and each outfit product contributes the variant
 * carrying that same size. A product with no size axis, or no variant in that
 * size, falls back to its first available variant.
 *
 * The cart event plumbing is wrapped so that a change in the theme's event API
 * can never stop the actual add to cart from working.
 */
class StlnCompleteTheLookComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    super.disconnectedCallback();
  }

  get addButton() {
    return this.querySelector('[data-stln-ctl-add]');
  }

  get messageElement() {
    return this.querySelector('[data-stln-ctl-message]');
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('[data-stln-ctl-add]')) return;
    event.preventDefault();
    this.#addOutfit();
  };

  /** @param {string} name */
  #json(name) {
    try {
      return JSON.parse(this.dataset[name] || 'null');
    } catch {
      return null;
    }
  }

  /**
   * The size currently selected on this page, lowercased, or null.
   * @returns {string | null}
   */
  #selectedSize() {
    const sizes = this.#json('currentSizes') || {};

    for (const input of document.querySelectorAll('.product-information input[name="id"]')) {
      // Quick add forms inside this very block also carry a variant id input.
      if (input.closest('stln-quick-add-component')) continue;
      if (input instanceof HTMLInputElement && input.value && sizes[input.value]) {
        return sizes[input.value];
      }
    }

    const fromUrl = new URLSearchParams(window.location.search).get('variant');
    return (fromUrl && sizes[fromUrl]) || null;
  }

  /** @param {string} [text] */
  #showMessage(text) {
    const element = this.messageElement;
    if (!element) return;
    if (!text) {
      element.hidden = true;
      return;
    }
    element.textContent = text;
    element.hidden = false;
  }

  async #addOutfit() {
    const outfit = this.#json('outfit') || [];
    const size = this.#selectedSize();

    const items = [];
    for (const entry of outfit) {
      const matched = size && entry.sizes ? entry.sizes[size] : null;
      const id = Number(matched || entry.fallback || 0);
      if (id) items.push({ id, quantity: 1 });
    }

    if (!items.length) {
      this.#showMessage(this.dataset.errorMessage);
      return;
    }

    const button = this.addButton;
    if (button instanceof HTMLButtonElement) button.disabled = true;
    this.#showMessage();

    let deferred = null;
    try {
      deferred = CartLinesUpdateEvent.createPromise?.() ?? null;
      if (deferred) {
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
            promise: deferred.promise,
          })
        );
      }
    } catch {
      deferred = null;
    }

    try {
      const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((element) => element.dataset.sectionId)
        .filter(Boolean);

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      if (result.status) throw new Error(result.description || result.message || 'Add to cart failed');

      let cart = null;
      const cartItems = document.querySelector('cart-items-component');
      if (cartItems) {
        await customElements.whenDefined('cart-items-component');
        cart = await cartItems.fetchCartData?.();
      }

      try {
        deferred?.resolve?.({
          cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse?.(cart) : undefined,
          detail: { source: 'stln-complete-the-look', itemCount: items.length },
        });
      } catch {
        // The add succeeded; only the optimistic event failed to settle.
      }
    } catch (error) {
      try {
        deferred?.reject?.(error);
      } catch {
        // ignored
      }
      this.#showMessage(this.dataset.errorMessage);
    } finally {
      if (button instanceof HTMLButtonElement) button.disabled = false;
    }
  }
}

if (!customElements.get('stln-complete-the-look-component')) {
  customElements.define('stln-complete-the-look-component', StlnCompleteTheLookComponent);
}
